import { CheckOutlined, StopOutlined } from '@ant-design/icons'
import { colors } from '@lmc/common/constants'
import type { Carer } from '@lmc/ts-common'
import { Transfer, type TransferProps } from 'antd'
import { Component, type CSSProperties } from 'react'
import { ProfilePicture } from '../../../generic'
import NextStepSection from '../components/NextStepSection'

interface TeamPermissionsProps {
  fields: any
  formId: string
  goToNextStep: () => void
  onFinishOnboarding: (params: { itemId: string }) => void
  home: string
  carers: Carer[]
}

interface TeamPermissionsState {
  dataSource: any[]
  targetKeys: string[]
}

export default class TeamPermissions extends Component<
  TeamPermissionsProps,
  TeamPermissionsState
> {
  constructor(props: TeamPermissionsProps) {
    super(props)
    this.state = {
      dataSource: [],
      targetKeys: []
    }
  }

  componentDidMount() {
    this.setData()
  }

  setData = () => {
    const { carers } = this.props
    const targetKeys: string[] = []
    const roleLookup: Record<string, string> = {
      'carehome-admin': 'Admin',
      'senior-carer': 'Senior',
      carer: 'Carer'
    }
    const dataSource = carers
      .filter((carer) => carer.active)
      .map((carer) => {
        const data = {
          key: carer.id,
          title: `${carer.name.first} ${carer.name.last}`,
          description: roleLookup[carer.role] || carer.role,
          chosen: carer.medicationPermission,
          picture: carer.picture
        }
        if (data.chosen) {
          targetKeys.push(data.key)
        }
        return data
      })

    this.setState({ dataSource, targetKeys })
  }

  // eslint-disable-next-line class-methods-use-this
  renderRow = (item: any) => {
    return (
      <div style={styles.carerRow as CSSProperties}>
        <div style={styles.profileContainer as CSSProperties}>
          <ProfilePicture
            type="small"
            url={item.picture}
            style={styles.image}
          />
          <div>{item.title}</div>
        </div>
        <div style={styles.roleContainer as CSSProperties}>
          {item.description}
        </div>
      </div>
    )
  }

  handleChange: TransferProps['onChange'] = (
    targetKeys,
    direction,
    moveKeys
  ) => {
    // @ts-ignore
    const { updateCarer } = this.props as unknown as any
    if (direction === 'right') {
      moveKeys.forEach((user) => {
        updateCarer({ itemId: user, permission: true })
      })
    } else {
      moveKeys.forEach((user) => {
        updateCarer({ itemId: user, permission: false })
      })
    }
    this.setState({ targetKeys: targetKeys as any })
  }

  // eslint-disable-next-line class-methods-use-this
  filterOption = (inputValue: string, option: { title: string }) =>
    option.title.toLowerCase().includes(inputValue.toLowerCase())

  render() {
    const { goToNextStep, onFinishOnboarding, home } = this.props

    return (
      <div style={styles.container as CSSProperties}>
        <div
          style={
            { ...styles.sectionContainer, marginBottom: 16 } as CSSProperties
          }
        >
          <div style={{ fontSize: 20 }}>Team Permissions</div>
          <div style={{ width: '100%', border: '1px solid #DDDDDD' }} />
        </div>

        <div style={styles.topText as CSSProperties}>
          Set up who should have permission to sign medication in the Carer App
          and manage medication in the Care Office.
        </div>

        <div
          className="antd-transfer-container"
          style={styles.sectionContainer as CSSProperties}
        >
          <div style={{ fontWeight: 'bold', display: 'flex' }}>
            <p style={{ minWidth: 564 }}>
              <StopOutlined style={{ paddingRight: 8, color: colors.red }} />
              Do <span style={{ color: colors.red }}>NOT</span> have medication
              permissions
            </p>
            <p>
              <CheckOutlined
                style={{ paddingRight: 8, color: colors.successgreen }}
              />
              Have medication permissions
            </p>
          </div>
          <Transfer
            dataSource={this.state.dataSource}
            showSearch
            listStyle={{
              height: 300,
              minWidth: 360,
              flex: 1
            }}
            filterOption={this.filterOption}
            operations={['Give permission', 'Remove permission']}
            targetKeys={this.state.targetKeys}
            onChange={this.handleChange}
            render={this.renderRow}
            style={{ width: '100%' }}
            locale={{
              itemUnit: 'Team Member',
              itemsUnit: 'Team Members',
              searchPlaceholder: 'Search by name'
            }}
          />
          <div style={styles.bottomSection as CSSProperties}>
            <div style={{ fontWeight: 'bold' }}>By default</div>
            <div>Admins and Seniors have medication permission</div>
            <div>Carers do not have medication permission</div>
          </div>
        </div>
        <NextStepSection
          onNext={() => {
            onFinishOnboarding({ itemId: home })
            goToNextStep()
          }}
          label="Complete Set Up"
        />
      </div>
    )
  }
}

const styles = {
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    margin: '16px auto 70px',
    maxWidth: 960,
    padding: '16px',
    position: 'relative'
  },
  topText: {
    marginBottom: 32,
    textAlign: 'left',
    width: '100%'
  },
  bottomText: {
    marginTop: 32,
    fontSize: 12
  },
  sectionContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  cardStyle: {
    width: '100%',
    minHeight: 320,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 32
  },
  image: {
    float: 'left',
    marginRight: '4px'
  },
  roleContainer: {
    background: colors.grey,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3px 8px'
  },
  carerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  profileContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'flex-start',
    marginTop: 32
  }
}
